import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { QRCodeSVG } from "qrcode.react";
import { REGENERATE_TABLE_TOKEN } from "../queries";

type Props = {
  tableId: number;
  branchId: number;
  tableName: string;
  orderUrl?: string | null;
  show: boolean;
  onClose: () => void;
  onTokenChange: (orderToken: string, orderUrl: string) => void;
};

const TableQrModal: React.FC<Props> = ({
  tableId,
  branchId,
  tableName,
  orderUrl,
  show,
  onClose,
  onTokenChange,
}) => {
  const [copied, setCopied] = useState(false);

  const [regenerateToken, { loading, error }] = useMutation(
    REGENERATE_TABLE_TOKEN
  );

  const handleGenerate = async () => {
    const { data } = await regenerateToken({
      variables: { id: tableId, branch_id: branchId },
    });
    const table = data?.regenerateTableToken;
    if (table) {
      onTokenChange(table.order_token, table.order_url);
    }
  };

  const handleCopy = () => {
    if (orderUrl) {
      navigator.clipboard.writeText(orderUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal isOpen={show} toggle={onClose}>
      <ModalHeader toggle={onClose}>
        <i className="fas fa-qrcode mr-2"></i>
        Codigo QR - Mesa {tableName}
      </ModalHeader>
      <ModalBody className="text-center">
        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle mr-1"></i>
            {error.message}
          </div>
        )}

        {orderUrl ? (
          <>
            <p className="text-muted mb-3">
              Los clientes pueden escanear este QR en la mesa para ordenar
              desde su celular, sin necesidad de crear una cuenta.
            </p>
            <div className="mb-3">
              <QRCodeSVG value={orderUrl} size={200} level="M" />
            </div>
            <label className="d-block text-left text-muted small mb-1">
              Enlace directo (para copiar, enviar por mensaje o grabar en un
              tag NFC)
            </label>
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                value={orderUrl}
                readOnly
                onFocus={(e) => e.target.select()}
              />
              <div className="input-group-append">
                <button
                  className={`btn ${
                    copied ? "btn-success" : "btn-outline-primary"
                  }`}
                  title="Copiar enlace"
                  onClick={handleCopy}
                >
                  <i className={`fas ${copied ? "fa-check" : "fa-copy"}`}></i>
                </button>
                <a
                  className="btn btn-outline-primary"
                  title="Enviar por mensaje de texto"
                  href={`sms:?body=${encodeURIComponent(orderUrl)}`}
                >
                  <i className="fas fa-sms"></i>
                </a>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Regenerando...
                </>
              ) : (
                "Regenerar QR"
              )}
            </button>
            <div>
              <small className="text-muted">
                Al regenerar, el QR impreso actual dejara de funcionar.
              </small>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted mb-3">
              Esta mesa aun no tiene un QR de pedidos. Generalo para que los
              clientes puedan ordenar directamente desde su celular.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Generando...
                </>
              ) : (
                "Generar QR"
              )}
            </button>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default TableQrModal;
